<template>
  <div class="flex justify-center items-center w-inherit pb-8 mt-20">
    <div class="flex flex-col justify-center items-center w-full md:mx-2">
      <div class="w-full" v-if="loading">
        <thread-loading-item></thread-loading-item>
        <div
          class="flex flex-col w-full my-2 bg-grey-0 pt-2 md:rounded-b-lg border-t border-blue-8"
        >
          <reply-loading-item v-for="(x, index) in randomArray" :key="index"></reply-loading-item>
        </div>
      </div>

      <div class="w-full" v-else>
        <thread-item :thread="thread" id="thread" class="shadow-md" />

        <div v-if="replies.data.length !== 0" class="w-full mt-2 border-t border-yellow-8">
          <div class="flex flex-col w-full my-2 bg-grey-0 md:rounded-b-lg shadow-md">
            <reply-item
              class="border-b border-yellow-1 | reply-item"
              v-for="reply in replies.data"
              :reply="reply"
              :thread="thread"
              :key="reply.id"
              :id="'reply-' + reply.id"
              @deleted="replyDeleted"
            />
          </div>

          <pagination :data="replies" route="threads.show" class="mt-4"></pagination>
        </div>

        <reply-create-form
          @success="replyCreated"
          v-if="$auth.check() && $auth.user().verified && !loading && (!thread.locked || authCanModify)"
        ></reply-create-form>
        <div
          class="text-center text-yellow-8"
          v-else-if="$auth.check() && (thread.locked && !authCanModify)"
        >This thread is locked and cannot be replied to.</div>
      </div>
    </div>

    <portal to="breadcrumb-portal">
      <breadcrumbs v-if="currentChannel && currentCategory && thread">
        <router-link class="breadcrumb-link" :to="{name: 'channels.index'}">Home</router-link>
        <chevron-right-icon class="mx-1"></chevron-right-icon>
        <span class="breadcrumb-item">{{ currentCategory.name }}</span>
        <chevron-right-icon class="mx-1"></chevron-right-icon>
        <router-link
          class="breadcrumb-link"
          :to="{name: 'channels.show', params: { channel: currentChannel.slug }}"
        >{{ currentChannel.name }}</router-link>
        <chevron-right-icon class="mx-1"></chevron-right-icon>
        <span class="breadcrumb-current">{{ thread.title }}</span>
        <chevron-right-icon v-if="page !== 1" class="mx-1"></chevron-right-icon>
        <span v-if="page !== 1" class="breadcrumb-current">Page {{ page }}</span>
      </breadcrumbs>
    </portal>
  </div>
</template>

<script>
import ReplyCreateForm from "@/components/Forms/ReplyCreateForm"
import ThreadItem from "@/components/Items/ThreadItem"
import ThreadLoadingItem from "@/components/Items/Loading/ThreadLoadingItem"
import ReplyItem from "@/components/Items/ReplyItem"
import ReplyLoadingItem from "@/components/Items/Loading/ReplyLoadingItem"
import Pagination from "@/components/Pagination"
import BreadcrumbHelper from "@/mixins/BreadcrumbHelper"
import { EventBus } from "@/events"
import { indexOf } from "lodash"
import { ChevronRightIcon } from "vue-feather-icons"

export default {
  name: "Thread",
  mixins: [BreadcrumbHelper],
  components: {
    ReplyCreateForm,
    ThreadItem,
    ThreadLoadingItem,
    ReplyItem,
    ReplyLoadingItem,
    Pagination,
    ChevronRightIcon
  },
  data() {
    return {
      loading: false,
      thread: null,
      page: 1,
      replies: {
        data: []
      },
      randomArray: Array.from({ length: Math.floor(Math.random() * 10) + 10 })
    }
  },
  watch: {
    $route: {
      handler() {
        if (this.$route.query.page) {
          this.page = parseInt(this.$route.query.page)
        }

        this.fetch(true)
      },
      immediate: true
    },
    thread: {
      handler(val) {
        if (val) {
          document.title = val.title + " - Nexus - The Pylon Show"
        }
      },
      immediate: true
    }
  },
  computed: {
    authCanModify() {
      if (this.$auth.check(["admin", "super-moderator"])) return true
      if (
        this.$auth.check(["moderator"]) &&
        this.$auth
          .user()
          .moderatable_channels.indexOf(this.$route.params.channel) !== -1
      )
        return true

      return false
    }
  },
  methods: {
    async fetch(loading = false) {
      if (loading) this.loading = true
      await this.$api.threads.show().then(({ data }) => (this.thread = data))
      await this.$api.replies.get(this.page).then(({ data }) => {
        if (data.data.length === 0 && this.page !== 1) {
          this.$router.replace({
            name: "threads.show",
            params: {
              category: this.$route.params.category,
              channel: this.$route.params.channel,
              thread: this.$route.params.thread
            },
            query: {
              page: 1
            }
          })
        } else {
          this.replies = data
          if (this.$route.hash) {
            this.$nextTick(() => {
              setTimeout(() => this.$scrollTo(this.$route.hash), 500)
            })
          }
        }
      })
      if (loading) this.loading = false
    },
    replyCreated(reply) {
      this.fetch().then(() => {
        let index = indexOf(this.thread.replies, reply.id) + 1,
          page = Math.ceil(index / 25)

        if (page === this.page) {
          this.$scrollTo("#reply-" + reply.id)
        } else {
          let to = {
            name: "threads.show",
            params: {
              category: this.$route.params.category,
              channel: this.$route.params.channel,
              thread: this.$route.params.thread
            },
            hash: "#reply-" + reply.id,
            query: {}
          }

          if (page > 1) to.query.page = page

          this.$router.push(to)
        }
      })
    },
    replyDeleted() {
      this.fetch().then(() => {
        if (this.replies.data.length === 0) {
          this.$router.push({
            name: "threads.show",
            params: {
              category: this.$route.params.category,
              channel: this.$route.params.channel,
              thread: this.$route.params.thread
            },
            query: {
              page: this.page - 1
            }
          })
        }
      })
    }
  },
  mounted() {
    EventBus.$on("auth:login", () => this.fetch())
    EventBus.$on("auth:logout", () => this.fetch())
  },
  beforeDestroy() {
    EventBus.$off("auth:login")
    EventBus.$off("auth:logout")
  }
}
</script>

<style scoped lang="scss">
.reply-item:first-child {
  @apply -mt-2;
}
.reply-item:last-child {
  @apply border-b-0;
}
</style>
